// EP-134 §2b (T134-007) — the ownership-history block on the unit-detail
// page. Renders every OwnerContract for the unit in reverse-chronological
// order, with a ⓘ tooltip on rows that came in via a transfer record
// (transferred_from_id is non-null AND the row points at the next row in
// chronological order).
//
// §3 D-3 — the history view is built from ENDED contracts, not from
// transfer records. Pre-EP-134 units show their past owners from rows
// that already exist; they simply have no ⓘ. We present them as
// history-without-a-transfer-record rather than implying one exists.
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui'
import { useUnitOwnershipHistory } from '@/hooks/useOwnerContracts'
import { useDateFormatter } from '@/hooks/useDateFormatter'

export interface OwnershipHistoryProps {
  unitId: number
}

export function OwnershipHistory({ unitId }: OwnershipHistoryProps) {
  const { t } = useTranslation('shared_owners')
  const { data: contracts, isLoading } = useUnitOwnershipHistory(unitId)
  const formatDate = useDateFormatter()

  if (isLoading) return null
  if (!contracts || contracts.length === 0) return null

  // Sort oldest first so the "next" pointer makes sense, then reverse for
  // display so the current owner is at the top.
  const chronological = [...contracts].sort(
    (a, b) => new Date(a.started_at).getTime() - new Date(b.started_at).getTime()
  )
  // Build a Set of contract ids that were the SOURCE of a transfer
  // (their id appears in some other row's transferred_from_id).
  const transferredFromIds = new Set<number>()
  chronological.forEach((row) => {
    if (row.transferred_from_id) transferredFromIds.add(row.transferred_from_id)
  })

  const displayed = [...chronological].reverse()

  return (
    <Card data-testid="ownership-history">
      <h2 className="text-lg font-semibold mb-4">
        {t('messages.history.title')}
      </h2>
      <ul className="divide-y divide-base-200">
        {displayed.map((row) => {
          const isTransferSource = transferredFromIds.has(row.id)
          const isCurrent = row.status === 'active'
          return (
            <li
              key={row.id}
              className="py-2 flex items-center gap-2 text-sm"
              data-testid={`ownership-history-row-${row.id}`}
            >
              <div className="flex-1 min-w-0">
                <Link
                  to={`/leases/${row.id}`}
                  className="font-medium hover:underline"
                >
                  {row.customer?.name || `Lease ${row.id}`}
                </Link>
              </div>
              <div className="text-base-content/60 whitespace-nowrap">
                {isCurrent
                  ? `${formatDate(row.started_at)} – ${t('messages.history.until_now')}`
                  : `${formatDate(row.started_at)} – ${formatDate(row.ended_at)}`}
              </div>
              {isTransferSource && (
                <span
                  className="text-base-content/40"
                  title={t('messages.history.transferred_from_tooltip')}
                  aria-label={t('messages.history.transferred_from_tooltip')}
                >
                  ⓘ
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </Card>
  )
}